import React from 'react'

import { Card, CardHeading, CardContent, CardFooter } from '@components/layout/Card'
import { HeatmapChart } from '@components/charts/HeatmapChart'
import { DoughnutChart } from '@components/charts/DoughnutChart'

import { Stack } from '@UILibrary/Stack'
import { Text } from '@UILibrary/Text'
import { Icon } from '@UILibrary/Icon'

import { heatMapChartOptionsProp } from './charts/options'
import { buildHeatMapChartDataProp, buildDoughnutChartDataProp } from './charts/data'

import { LearnMore } from '../../../Shared/LearnMore'

interface CohortFinancialHealth {
  labels: string[]
  names: string[]
  datasets: any[]
}

interface IncomeBreakdown {
  debt_percentage: number
  expenses_percentage: number
  investments_percentage: number
  remaining_percentage: number
  biggest_contribution_text: string
}

interface FinancialDistributionGroupProps {
  cohortFinancialHealth: CohortFinancialHealth
  incomeBreakdown: IncomeBreakdown
}

export const FinancialDistributionGroup: React.FC<FinancialDistributionGroupProps> = (props) => {
  const totalSpending = props.incomeBreakdown.debt_percentage +
                       props.incomeBreakdown.expenses_percentage +
                       props.incomeBreakdown.investments_percentage;
  const exceedsHundred = totalSpending > 100;

  return (
    <>
      <Stack horizontal spacing="m">
        {/* Financial Health Per Cohort Card */}
        <Card className='content-expand-chart'>
          <CardHeading>
            <Text heading4a colour="secondary-main" as="h3" margin="none">Retirement safety by cohort</Text>
            <Text body as="p" margin="none">We're tracking each individual cohort separately as they've joined the programme to account for time in programme.</Text>
          </CardHeading>
          <CardContent>
            <HeatmapChart
              height='small'
              options={
                heatMapChartOptionsProp(
                  props.cohortFinancialHealth.labels,
                  props.cohortFinancialHealth.names
                )
              }
              datasets={buildHeatMapChartDataProp(props.cohortFinancialHealth.datasets)}
            />
          </CardContent>
          <CardFooter>
            <LearnMore
              buttonText="How is the financial health score calculated?"
              heading="Financial Health Per Cohort"
              subHeading="Understanding the financial health of your organisation is critical."
            >
              <Text body as="p">
                In simplest terms, the financial health score looks at how much of a person's expenses they would be able to cover from investments and other sources should they one day stop working. This is typically referred to as their "replacement ratio".
              </Text>
              <Text body as="p">
                This number is a result of every decision that they make on their financial journey.
              </Text>
              <Text body as="p">
                We calculate this score for each individual, then we calculate the average for the cohort, then the average for all cohorts together.
              </Text>
              <Text body as="p">
                This metric will expand in the future, taking into consideration more aspects of each person's personal financial journey.
              </Text>
              <Text body as="p">
                The financial health score is dependent on the accuracy of the data entered into the system.
              </Text>
            </LearnMore>
          </CardFooter>
        </Card>

        {/* Income Breakdown Card */}
        <Card>
          <CardHeading>
            <Text heading4a colour="secondary-main" as="h3" margin="none">
              Where is your team's money going?
            </Text>
            <Text body as="p" margin="none">
              Based on your team's inputs, most of their income is spent on <Text as="strong">{props.incomeBreakdown.biggest_contribution_text}</Text>.
            </Text>
            <Text body as="p" margin="none">
              Anything not part of their inputs will be shown here as "unaccounted for".
            </Text>
          </CardHeading>
          <CardContent>
            <Stack horizontal spacing="m">
              <DoughnutChart
                height="small"
                data={buildDoughnutChartDataProp([
                  props.incomeBreakdown.debt_percentage,
                  props.incomeBreakdown.expenses_percentage,
                  props.incomeBreakdown.investments_percentage,
                  props.incomeBreakdown.remaining_percentage
                ])}
                options={{ plugins: { legend: { display: false } } }}
                style={{ flex: 1 }}
              />
              <div className='mauto' style={{ flex: 1 }}>
                <Text bodyLarge as="p" margin='none'>
                  <Icon name="block" size="medium" margin={{ right: 's' }} color="salmon-pink" />
                  <Text heading4a as="strong" margin={{ right: 'xxs' }}>
                    {props.incomeBreakdown.debt_percentage}%
                  </Text>
                  of income is spent on debt
                </Text>
                <Text bodyLarge as="p" margin='none'>
                  <Icon name="block" size="medium" margin={{ right: 's' }} color="golden-rod" />
                  <Text heading4a as="strong" margin={{ right: 'xxs' }}>
                    {props.incomeBreakdown.expenses_percentage}%
                  </Text>
                  of income is spent on other expenses
                </Text>
                <Text bodyLarge as="p" margin='none'>
                  <Icon name="block" size="medium" margin={{ right: 's' }} color="forest-green" />
                  <Text heading4a as="strong" margin={{ right: 'xxs' }}>
                    {props.incomeBreakdown.investments_percentage}%
                  </Text>
                  of income is invested
                </Text>
                <Text bodyLarge as="p" margin='none'>
                  <Icon name="block" size="medium" margin={{ right: 's' }} color="grey" />
                  <Text heading4a as="strong" margin={{ right: 'xxs' }}>
                    {props.incomeBreakdown.remaining_percentage}%
                  </Text>
                  of income is unaccounted for
                </Text>
              </div>
            </Stack>
            {exceedsHundred && (
              <Text body as="p" margin="none">
                <Icon name="info" size="small" margin={{ right: 'xs' }}/>
                Your team utilized debt or additional capital (e.g., inheritance) to increase spending, which is why the total exceeds 100%
              </Text>
            )}
          </CardContent>
        </Card>
      </Stack>
    </>
  )
}
